import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/** react-router's position in the history stack, 0 on the entry the app loaded on. */
export const historyIdx = () => (window.history.state as { idx?: number } | null)?.idx ?? 0;

type NavigateUpOptions = {
  /** Router state for `to`, used only when it replaces this entry. */
  state?: unknown;
  /** Pages to leave, when the ones behind are gone too (a form over an item it just deleted). */
  levels?: number;
};

/**
 * Leave a page the way a phone's back gesture does: step back to wherever the user came from,
 * even when that is two levels up (Manage → a crew member goes back to Manage, not the roster).
 * With nothing of the app's behind this entry — a deep link, a notification, a reload — there is
 * nowhere to step back to, so the parent `to` takes this entry's place instead.
 */
export const useNavigateUp = () => {
  const navigate = useNavigate();
  return useCallback(
    (to: string, { state, levels = 1 }: NavigateUpOptions = {}) => {
      if (historyIdx() >= levels) void navigate(-levels);
      else void navigate(to, { replace: true, state });
    },
    [navigate],
  );
};
